import useLocalstoragePlaintext from "hooks/useLocalstoragePlainText";
import { useQuery, useQueryClient } from "react-query";
import { CustomerId, UserContext } from "services/motorpoint/user-context-api/types";
import { logInfo } from "utils/logger";
import { v4 as uuid } from "uuid";

import useUserContextApi from "./useUserContextApi";

const LEGACY_LS_KEY = "shortlistId";
const USER_CONTEXT_LS_KEY = "userContextId";

const useUserContext = () => {
    const queryClient = useQueryClient();
    const [legacyId, _, removeLegacyId] = useLocalstoragePlaintext(LEGACY_LS_KEY, "");
    const [userContextId, setUserContextId] = useLocalstoragePlaintext(USER_CONTEXT_LS_KEY, "");
    const { getByCustomerId, getById } = useUserContextApi();

    const { data: userContext } = useQuery({
        refetchOnWindowFocus: false,
        queryKey: "userContext",
        queryFn: () => queryClient.getQueryData<UserContext>("userContext"),
        retry: 3,
        retryDelay: (attemptIndex, error) => {
            return Math.pow(2, attemptIndex) * 200;
        },
        cacheTime: Infinity,
    });

    const initUserContextAnonymous = async () => {
        let ucId = userContextId || legacyId || uuid();

        if (legacyId) {
            removeLegacyId();
        }

        setUserContextId(ucId);

        if (ucId && !userContext) {
            await getById({
                userContextId: ucId,
            });

            logInfo("Set userContextId (anonymousUserContextId): ", ucId);
        }
    };

    const initUserContextForCustomer = async (params: CustomerId) => {
        const userContextResponse: UserContext | undefined = await getByCustomerId(params);

        if (userContextResponse) {
            setUserContextId(userContextResponse.userContextId);

            logInfo(
                "Set userContextId (customerUserContextId): ",
                userContextResponse.userContextId
            );
        } else {
            initUserContextAnonymous();
        }
    };

    return {
        userContext,
        userContextId,
        setUserContextId,
        initUserContextForCustomer,
        initUserContextAnonymous,
    };
};

export default useUserContext;
